<template>
  <section :class="[$options.name, { mobile: isMobile }, { disabled: busy }]">
    <p v-if="loginError" class="tw:mb-2" style="font-size: 0.875rem; color: red">
      {{ loginError }}
    </p>
    <EdroneInput
      id="email-input"
      v-model.trim="v$.userEmail.$model as unknown as string"
      :error-msg="
        v$.userEmail.$dirty && v$.userEmail.$error
          ? translations.inputs.email.errors[v$.userEmail.$errors[0].$message as string]
          : null
      "
      :invalid="v$.userEmail.$dirty && (v$.userEmail.email.$invalid || v$.userEmail.required.$invalid)"
      :label="translations.inputs.email.label"
      :placeholder="translations.inputs.email.placeholder"
      class="required-inputs tw:mb-4"
      type="email"
      unique-id="b07130e5-2251-4b5c-9cac-4c020163f842"
      @enter="handleNextInput"
    />
    <EdroneInput
      v-model.trim="v$.userPassword.$model as unknown as string"
      :error-msg="
        v$.userPassword.$dirty && v$.userPassword.$error
          ? translations.inputs.password.errors[v$.userPassword.$errors[0].$message as string]
          : null
      "
      :invalid="v$.userPassword.$dirty && v$.userPassword.required.$invalid"
      :label="translations.inputs.password.label"
      :placeholder="translations.inputs.password.placeholder"
      class="required-inputs tw:mb-2"
      type="password"
      unique-id="d8c3547a-b608-4734-ac0e-a3dc7d16105a"
      @enter="handleNextInput"
    />

    <a class="tw:text-blue-500 tw:text-sm tw:no-underline tw:float-right tw:mb-4" href="/forgot_password">
      {{ translations.buttons.password }}
    </a>

    <EdroneButton
      :disabled="!userEmail || !userPassword"
      class="required-inputs tw:mb-4"
      style="width: 100%"
      type="solid"
      unique-id="c4291173-4ca9-41dc-bbd3-205a7362420a"
      @click="printResult"
    >
      {{ translations.buttons.login }}
    </EdroneButton>

    <div class="tw:flex tw:items-center tw:gap-3 tw:mb-4">
      <div class="tw:flex-1 tw:h-px tw:bg-gray-300"></div>
      <span class="tw:text-gray-500 tw:text-sm">{{ translations.google?.or || "or" }}</span>
      <div class="tw:flex-1 tw:h-px tw:bg-gray-300"></div>
    </div>

    <GoogleSignInButton
      :translations="translations.google"
      class="tw:mb-4"
      context="login"
      @error="handleGoogleError"
      @success="handleGoogleSignIn"
    />

    <div class="tw:flex tw:justify-center tw:mb-4">
      <a
        class="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:border tw:border-gray-300 tw:rounded tw:bg-white tw:text-gray-700 tw:text-sm tw:font-medium tw:no-underline hover:tw:bg-gray-50 tw:transition-colors"
        href="https://admin.shopify.com/apps/edrone/shopify/app/login-edrone"
        style="width: 278px; height: 38px"
      >
        <svg height="20" viewBox="0 0 256 292" width="20" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M223.774 57.34c-.201-1.46-1.48-2.268-2.537-2.357-1.055-.088-23.383-1.743-23.383-1.743s-15.507-15.395-17.209-17.099c-1.703-1.703-5.029-1.185-6.32-.805-.19.056-3.388 1.043-8.678 2.68-5.18-14.906-14.322-28.604-30.405-28.604-.444 0-.901.018-1.358.044C129.31 3.407 123.644 0 118.85 0c-37.54 0-55.529 46.835-61.16 70.67l-26.272 8.117c-8.078 2.532-8.355 2.78-9.412 10.39C21.093 95.73 0 256.236 0 256.236l175.876 33.03L256 266.75s-32.025-207.942-32.226-209.412v.002zM164.806 44.51a815.396 815.396 0 0 0-14.518 4.482c-.002-10.534-1.458-25.323-6.38-37.56 15.821 2.998 23.515 20.923 20.898 33.078zm-29.123 8.996c-9.859 3.049-20.656 6.385-31.422 9.713C109.874 42.135 121.123 27.318 133.69 21.893c4.661 4.29 9.983 14.538 1.993 31.613zM118.651 9.054c2.563 0 5.121.76 7.468 2.273-15.86 7.467-32.86 26.315-40.077 64.04l-24.88 7.693c6.876-23.297 23.488-74.006 57.489-74.006z"
            fill="#95BF47"
          />
          <path
            d="M221.237 54.983c-1.055-.088-23.383-1.743-23.383-1.743s-15.507-15.395-17.209-17.099c-.637-.634-1.496-.959-2.394-1.099l-2.374 48.674 80.123-17.356s-32.025-207.942-32.226-209.412c-.201-1.46-1.48-2.268-2.537-2.357z"
            fill="#5E8E3E"
            transform="translate(0 145.392)"
          />
          <path
            d="M133.702 99.936l-11.063 32.866s-9.728-5.157-21.612-5.157c-17.465 0-18.348 10.947-18.348 13.713 0 15.068 39.305 20.848 39.305 56.162 0 27.78-17.623 45.665-41.396 45.665-28.515 0-43.118-17.734-43.118-17.734l7.639-25.228s15.001 12.882 27.66 12.882c8.265 0 11.652-6.502 11.652-11.254 0-19.657-32.252-20.536-32.252-52.894 0-27.218 19.553-53.576 59.004-53.576 15.233 0 22.529 4.555 22.529 4.555z"
            fill="#FFF"
          />
        </svg>
        {{ translations.shopify.continueWith }}
      </a>
    </div>

    <p v-if="!isEnabledWebView" class="tw:text-sm tw:text-center">
      {{ translations.register.text }}
      <a :href="'/register' + query" class="tw:text-blue-500 tw:no-underline" @click="$emit('register')">
        {{ translations.register.action }}
      </a>
    </p>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import axios from "vue-app/utils/axios";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneInput from "edrone-components/EdroneInput.vue";
import { ReactNativeBridge } from "vue-app/utils/reactNativeBridge";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";
import { useCaptcha } from "vue-app/composables/useCaptcha.ts";
import { type GoogleUserData } from "vue-app/composables/useGoogleSignIn/useGoogleSignIn.composable";
import GoogleSignInButton from "vue-app/components/Account/GoogleSignInButton/GoogleSignInButton.vue";

export default defineComponent({
  name: "AccountLoginForm",
  components: {
    EdroneInput,
    EdroneButton,
    GoogleSignInButton,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["register"],
  setup() {
    const query = window.location.search;
    const translations = window._trans._account.login;
    const refRecaptchaDiv = ref(null);

    const userEmail = ref("");
    const userPassword = ref("");
    const loginError = ref("");

    const rules = {
      userEmail: { required, email },
      userPassword: { required },
    };
    const v$ = useVuelidate(rules, { userEmail, userPassword });

    function handleNextInput() {
      const inputs = document.querySelectorAll(".required-inputs input, .required-inputs.edrone-button");
      const activeElement = document.activeElement as HTMLElement;
      const index = Array.from(inputs).indexOf(activeElement);
      if (index === 0) {
        v$.value.userEmail.$touch();
      }
      if (index === 1) {
        v$.value.userPassword.$touch();
      }
      (inputs[index + 1] as HTMLElement)?.focus();
      if (index === 1 && !!userPassword.value) {
        printResult();
      }
    }

    const busy = ref<boolean>(false);

    function printResult() {
      v$.value.userEmail.$touch();
      v$.value.userPassword.$touch();

      if (!v$.value.userEmail.$error && !v$.value.userPassword.$error) {
        loginUser();
      }
    }

    async function loginUser() {
      try {
        amplitudeSendEvent("login-button-click");
        const { executeRecaptcha } = useCaptcha();
        const token = await executeRecaptcha("LOGIN");
        if (!token) {
          loginError.value = translations.errors.other;
          busy.value = false;
          return;
        }
        busy.value = true;
        loginError.value = "";
        let bodyFormData = new FormData();
        bodyFormData.append("email", userEmail.value);
        bodyFormData.append("password", userPassword.value);
        bodyFormData.append("g-recaptcha-response", token);
        bodyFormData.append("_remember_me", "true");

        const response = await axios({
          method: "post",
          url: "/login",
          data: bodyFormData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.status === "success") {
          amplitudeSendEvent("login-success");
          window.location.href = "/login/trackers";
        } else {
          amplitudeSendEvent("login-error");

          if (response.data.status === "error") {
            if (response.data.message === "Your email address is not assigned to any workspace") {
              loginError.value = translations.errors.unlinked;
            } else {
              loginError.value = translations.errors.invalid;
            }
          } else {
            loginError.value = translations.errors.expired;
          }
          busy.value = false;
        }
      } catch (error) {
        loginError.value = translations.errors.other;
        busy.value = false;
      }
    }

    async function handleGoogleSignIn(userData: GoogleUserData) {
      busy.value = true;
      loginError.value = "";
      amplitudeSendEvent("google-login-button-click");

      try {
        const bodyFormData = new FormData();
        bodyFormData.append("google_token", userData.token);

        const response = await axios({
          method: "post",
          url: "/login/google",
          data: bodyFormData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.status === "success") {
          amplitudeSendEvent("google-login-success");
          window.location.href = "/login/trackers";
        } else {
          amplitudeSendEvent("google-login-error", { error_type: response.data.error_type });

          if (response.data.error_type === "email_exists_not_linked") {
            loginError.value =
              translations.google?.emailExistsNotLinked ||
              "An account with this email exists. Please log in with your password and link your Google account in settings.";
          } else if (response.data.error_type === "account_not_found") {
            loginError.value =
              translations.google?.accountNotFound || "No account found with this email. Please register first.";
          } else {
            loginError.value = response.data.message || translations.errors.other;
          }
          busy.value = false;
        }
      } catch (error) {
        amplitudeSendEvent("google-login-error", { error_type: "network_error" });
        loginError.value = translations.errors.other;
        busy.value = false;
      }
    }

    function handleGoogleError(errorMessage: string) {
      loginError.value = errorMessage;
      busy.value = false;
    }

    const isEnabledWebView = ReactNativeBridge.checkIsWebViewMode();

    return {
      query,
      printResult,
      refRecaptchaDiv,
      busy,
      translations,
      userEmail,
      userPassword,
      v$,
      handleNextInput,
      loginError,
      isEnabledWebView,
      handleGoogleSignIn,
      handleGoogleError,
    };
  },
});
</script>
